// Shared primitives cho đơn in (print orders) — dùng chung bởi
// PrintOrdersManager (tab Đơn in) và PrintOrderForm (form tạo đơn in), cũng như
// CskhOrderView (nút "Thêm đơn in" trong /cskh/$id). Tách ra để tránh trùng lặp
// định nghĩa bước in / helper tính tiền giữa các màn hình.

import type { CskhPrintOrder } from "./api";
import type { Customer } from "@/features/customers/api";

/**
 * Đơn gốc mà một đơn in gắn vào — phần form cần: gói (lọc danh mục), thuế
 * suất (tạm tính), và vài trường hiển thị. Cả `Order` (chi tiết) lẫn
 * `OrderSummary` (kết quả tìm) đều khớp, nên form không phải tải chi tiết đơn
 * chỉ để lấy hai cột thuế.
 */
export type PrintOrderBaseOrder = {
  id: string;
  code: string;
  customer: string;
  phone?: string | null | undefined;
  category: string;
  product: string;
  vatEnabled: boolean;
  vatRateBps: number;
  shootDate?: string | null | undefined;
};

/**
 * Mục tiêu của một đơn in đang lập: gắn vào ĐƠN có sẵn, hay lập cho KHÁCH chưa
 * có đơn (máy chủ tự lập đơn gốc ở `branch` — QĐ 0108). `branch` rỗng = chưa
 * chọn xong cơ sở, nút Tạo còn khoá.
 */
export type PrintOrderTarget =
  | { kind: "order"; order: PrintOrderBaseOrder }
  | { kind: "customer"; customer: Customer; branch: string };

/**
 * Gói của đơn gốc máy chủ tự lập khi lập đơn in cho khách CHƯA CÓ ĐƠN
 * (POST /cskh/print-intake, quyết định 0108). Bản sao của
 * `printintake.OrderCategory` / `DefaultProduct` (backend/internal/core/cskh/
 * printintake/model.go) — form cần biết để lọc danh mục bán thêm đúng như máy
 * chủ sẽ lọc: chỉ mục áp "mọi đơn" mới bán được trên đơn này.
 */
export const PRINT_INTAKE_CATEGORY = "print";
export const PRINT_INTAKE_PRODUCT = "In ảnh (khách cũ)";

// Tám bước của một đơn in (spec §3, QĐ 0097). Máy chủ TÍNH ra bước này từ mốc
// thật (gửi mẫu, gửi nhà in, kiểm từng món, giao khách) — không ai bấm chọn
// nữa. Nhãn viết như người nói với nhau: người đọc là lễ tân cơ sở, không phải
// người viết mã.
export const PRINT_STEPS = [
  { key: "created", label: "Mới tạo", color: "#8452F5" },
  { key: "demo", label: "Chờ khách duyệt mẫu", color: "#6063F1" },
  { key: "printing", label: "Đang in", color: "#C15205" },
  { key: "arrived", label: "Hàng đã về, chờ kiểm", color: "#0C855D" },
  { key: "returned", label: "Đã trả nhà in làm lại", color: "#B45309" },
  { key: "ready", label: "Đạt, chờ giao khách", color: "#178640" },
  { key: "shipping", label: "Đã gửi ship", color: "#2563EB" },
  { key: "delivered", label: "Khách đã nhận", color: "#64748B" },
] as const;
export const FIRST_STEP = PRINT_STEPS[0].key;

export const STEP_LABEL: Record<string, string> = Object.fromEntries(
  PRINT_STEPS.map((s) => [s.key, s.label]),
);

/**
 * LEGACY_STEP_MAP — mã bước của ERP cũ (Appwrite) và của bộ 5 bước giữa chừng,
 * quy về bộ 8 bước hiện tại.
 *
 * Migration 0195 kéo 9 mã Appwrite về bộ 5; migration 0218 kéo `review` sang
 * `arrived`. Bảng này VẪN phải sống: máy chủ nhận bất kỳ chuỗi nào cho `step`
 * (cố ý — giao diện giữ từ vựng), nên một bản ghi mang mã cũ vẫn có thể xuất
 * hiện ở môi trường chưa chạy migration. Thiếu lưới này thì màn hình in thẳng
 * mã tiếng Anh cho nhân viên đọc ("wait_delivery") và bộ lọc theo bước khớp 0
 * dòng — đúng cảnh đo được ngày 01/09/2026.
 *
 * 🔴 `shipping` CỐ Ý KHÔNG còn trong bảng này. Trước 0218 nó là mã cũ (0195 đổ
 * về "delivered"); từ 0218 nó là BƯỚC THẬT. Thêm lại dòng đó là đơn vừa gửi
 * ship hiện "Khách đã nhận" — sai mà không lỗi nào nổi lên.
 *
 * Ánh xạ khớp TỪNG DÒNG với migrations/0195_print_order_step_vocab.up.sql và
 * phần backfill của 0218_print_ops.up.sql. Sửa một bên phải sửa bên kia.
 */
export const LEGACY_STEP_MAP: Record<string, string> = {
  request: "created",
  wait_retouch: "demo",
  return_fix: "demo",
  sent_printer: "printing",
  wait_delivery: "printing",
  qc_check: "arrived",
  review: "arrived",
  confirmed: "delivered",
};

/** normalizeStep — mã cũ về mã mới; mã mới (và mã lạ) giữ nguyên. */
export const normalizeStep = (step: string): string =>
  LEGACY_STEP_MAP[step] ?? step;

/**
 * stepLabel — nhãn tiếng Việt của một bước, đi qua bảng quy đổi trước.
 * Mọi chỗ hiển thị bước PHẢI gọi hàm này, đừng tra STEP_LABEL trực tiếp.
 */
export const stepLabel = (step: string): string =>
  STEP_LABEL[normalizeStep(step)] ?? step;

/** stepColor — màu của bước, cũng đi qua bảng quy đổi. */
export const stepColor = (step: string): string =>
  PRINT_STEPS.find((s) => s.key === normalizeStep(step))?.color ?? "#64748b";

/**
 * stepLabelWithRound — nhãn bước có kèm số lần trả nhà in ("lần 2"). Chỉ bước
 * `returned` mang số; các bước khác trả nhãn thường. Số lần là thông tin lễ tân
 * cần nhất khi nhìn danh sách: hàng trả đi trả lại lần thứ ba là chuyện phải
 * gọi điện, không phải chuyện chờ thêm.
 */
export const stepLabelWithRound = (step: string, round: number): string => {
  const base = stepLabel(step);
  if (normalizeStep(step) !== "returned" || !round || round <= 0) return base;
  return `${base} (lần ${round})`;
};

/**
 * partialLabel — câu phụ "đã giao trước x/y món" cho đơn giao làm nhiều đợt.
 * Máy chủ chỉ trả `partial` khi thật sự có giao trước (Task 4 `PartialOf`), nên
 * rỗng/không có nghĩa là giao đủ một lần — KHÔNG in "0/0 món".
 */
export const partialLabel = (
  partial: { delivered: number; total: number } | null | undefined,
): string => (partial ? `đã giao trước ${partial.delivered}/${partial.total} món` : "");

// PoItem — 1 dòng sản phẩm in. Form áp cùng 1 thuế suất cho mọi dòng của đơn,
// nhưng `vatEnabled` + `vatRateBps` vẫn nằm trên từng item vì đó là snapshot:
// đơn cũ giữ nguyên thuế suất đã chốt kể cả khi settings `vat_config` đổi về
// sau. Dòng cũ (chưa có 2 field này) decode thành false / 0 = không VAT.
export type PoItem = {
  productId: string;
  size: string;
  qty: number;
  unitPrice: number;
  vatEnabled: boolean;
  vatRateBps: number;
  /** Tấm ảnh hoàn thiện của đơn mà dòng này in (QĐ 0290); rỗng = không gắn. */
  photoId?: string;
  /** "Ảnh số N" do máy chủ tra lúc lập dòng; 0 = không gắn. */
  photoSeq?: number;
  /**
   * Mã ẢNH KHÁCH TỰ GỬI qua Cổng (QĐ 0300) — khớp `externalRef` của cửa
   * /print-orders/{id}/photos. Loại trừ với `photoId`; rỗng = không có.
   */
  externalRef?: string;
};

export const emptyItem = (): PoItem => ({
  productId: "",
  size: "",
  qty: 1,
  unitPrice: 0,
  vatEnabled: false,
  vatRateBps: 0,
});


/** Kẹp thuế suất về [0, 10000] bps (0–100%) — khớp validate của BE. */
export function clampRateBps(v: unknown): number {
  const n = Math.round(Number(v ?? 0));
  if (!Number.isFinite(n)) return 0;
  return Math.min(10_000, Math.max(0, n));
}

function externalRefOf(v: unknown): string {
  const ref = (v as { ref?: unknown } | null | undefined)?.ref;
  return typeof ref === "string" ? ref : "";
}

export function itemsOf(po: CskhPrintOrder): PoItem[] {
  const list = (po.items as { list?: unknown } | undefined)?.list;
  if (!Array.isArray(list)) return [];
  return list
    .filter((x): x is Record<string, unknown> => !!x && typeof x === "object")
    .map((x) => {
      const o = x as Record<string, unknown>;
      return {
        productId: String(o.productId ?? ""),
        size: String(o.size ?? ""),
        qty: Number(o.qty ?? 0),
        unitPrice: Number(o.unitPrice ?? 0),
        vatEnabled: Boolean(o.vatEnabled),
        vatRateBps: clampRateBps(o.vatRateBps),
        ...(typeof o.photoId === "string" && o.photoId ? { photoId: o.photoId } : {}),
        ...(Number(o.photoSeq) > 0 ? { photoSeq: Number(o.photoSeq) } : {}),
        ...(externalRefOf(o.externalPhoto) ? { externalRef: externalRefOf(o.externalPhoto) } : {}),
      };
    });
}

/**
 * Link ảnh in CHUNG của đơn.
 *
 * Từ 25/09/2026 link chung nằm ở `items.printLink`, còn `items.list[].printLink`
 * để dành cho link RIÊNG của từng món (backend/…/printorder/links.go). Bước lùi
 * về dòng đầu có link giữ cho đơn BẢN CŨ vẫn đọc ra link — máy chủ chuẩn hoá
 * lúc đọc, nhưng dữ liệu dựng tay trong bài kiểm thì không.
 */
export function orderPrintLink(po: CskhPrintOrder): string {
  const items = po.items as { list?: unknown; printLink?: unknown } | undefined;
  const common = String(items?.printLink ?? "").trim();
  if (common) return common;
  const list = items?.list;
  if (!Array.isArray(list)) return "";
  for (const x of list) {
    if (!x || typeof x !== "object") continue;
    const link = String((x as Record<string, unknown>).printLink ?? "").trim();
    if (link) return link;
  }
  return "";
}

/**
 * Link thật sự dùng cho món thứ i: link riêng nếu có, không thì link chung.
 * Đây là thứ thợ mang đi in — màn hình phải hiện đúng nó, không phải ô trống.
 */
export function itemPrintLink(po: CskhPrintOrder, index: number): string {
  const list = (po.items as { list?: unknown } | undefined)?.list;
  if (Array.isArray(list) && list[index] && typeof list[index] === "object") {
    const own = String((list[index] as Record<string, unknown>).printLink ?? "").trim();
    if (own) return own;
  }
  return orderPrintLink(po);
}

/**
 * Tiền VAT của MỘT dòng tiền, làm tròn nửa lên — bản sao của `surchargeVAT`
 * (backend/internal/core/order/vat.go).
 *
 * Phải tính TỪNG DÒNG rồi mới cộng, đừng gộp rồi mới nhân: máy chủ chụp VAT
 * riêng cho dòng hàng và dòng ship (`AddManagedLinesInTx`), nên gộp trước sẽ
 * lệch một đồng ở những cặp số mà hai lần làm tròn không trùng một lần.
 */
export function lineVAT(amount: number, rateBps: number): number {
  const rate = clampRateBps(rateBps);
  if (amount <= 0 || rate <= 0) return 0;
  return Math.round((amount * rate) / 10_000);
}

/** Tiền VAT của 1 dòng — tính trên thành tiền của dòng đó, không gồm ship/giảm giá. */
export function itemVAT(it: PoItem): number {
  if (!it.vatEnabled) return 0;
  return lineVAT(it.qty * it.unitPrice, it.vatRateBps);
}

/** Tổng VAT của danh sách sản phẩm in. */
export function itemsVAT(items: PoItem[]): number {
  return items.reduce((s, it) => s + itemVAT(it), 0);
}

// Thuế suất hiển thị cho cả đơn. Đơn mới luôn có 1 rate chung; đọc từ dòng chịu
// thuế đầu tiên vẫn đúng cho cả đơn cũ ghi trước khi rate được áp đồng loạt.
export function poVATRateBps(items: PoItem[]): number {
  return items.find((it) => it.vatEnabled)?.vatRateBps ?? 0;
}

export function poVAT(po: CskhPrintOrder): number {
  return itemsVAT(itemsOf(po));
}

export function poTotal(po: CskhPrintOrder): number {
  const list = itemsOf(po);
  const items = list.reduce((s, it) => s + it.qty * it.unitPrice, 0);
  const ship = po.shippingPayer === "customer" ? (po.shippingCost ?? 0) : 0;
  return Math.max(0, items + ship - (po.discount ?? 0) + itemsVAT(list));
}

/**
 * Số phải thu hiển thị. Đơn mới (kế hoạch A) = `payable` server: Σ(amount + VAT)
 * hai dòng phát sinh sống — đã gồm ship/giảm giá/VAT theo đơn. Bản cũ = tính tay
 * theo items như trước (ngoài sổ phát sinh).
 */
export function poPayable(po: CskhPrintOrder): number {
  return po.legacy ? poTotal(po) : po.payable;
}

/**
 * Cửa thu tiền của một đơn in. Năm vế, mỗi vế bịt một cách ghi sai sổ:
 *   · đã huỷ  → không còn gì để thu;
 *   · đã thu  → thu lần nữa là phiếu thừa (đơn hoàn tiền vẫn `paid` vì mốc là
 *               lịch sử — muốn bán lại thì huỷ rồi tạo đơn mới);
 *   · đang mang phiếu → máy chủ nhận MỘT phiếu cho mỗi đơn in. Phiếu chuyển
 *               khoản còn chờ kế toán thì đơn vẫn chưa "đã thu", mời thu nữa là
 *               khách trả hai lần và phiếu thứ hai ăn lỗi lúc gắn, nằm lại
 *               trên đơn gốc (cùng luật `hasTicket` của màn CSKH);
 *   · 0 đồng  → không mở hộp thu cho số không;
 *   · dòng hàng chưa duyệt → số phải thu ĐÃ trừ khoản giảm giá chưa ai gật.
 *               Thu theo số đó rồi trưởng từ chối là thu thiếu mà sổ ghi "đã
 *               thu". Bản cũ (`legacy`) không có dòng phát sinh nên miễn vế này
 *               và vế phiếu (máy chủ không gắn phiếu cho bản cũ).
 *
 * Dùng chung ở màn Đơn hàng và khối "Tiền đơn in" của màn Hàng in về cơ sở
 * (gói I màn 4) — hai nút Thu tiền phải mở và khoá theo cùng một luật.
 */
export function canCollectPrintOrder(po: CskhPrintOrder): boolean {
  if (po.cancelledAt || po.paid) return false;
  if (ticketPending(po)) return false;
  if (poPayable(po) <= 0) return false;
  return po.legacy || po.goodsStatus === "active";
}

/** Đơn in mới đang mang phiếu thu mà chưa "đã thu": phiếu chờ kế toán xác nhận
 *  (hoặc đã bị từ chối) — thay nút Thu tiền bằng câu `TICKET_PENDING_NOTE`. */
export function ticketPending(po: CskhPrintOrder): boolean {
  return !po.legacy && !!po.paymentId && !po.paid && !po.cancelledAt;
}

export const TICKET_PENDING_NOTE =
  "Đã gắn phiếu thu — chờ kế toán xác nhận. Mỗi đơn in chỉ nhận một phiếu: phiếu bị từ chối thì báo kế toán xử lý chính phiếu đó, đừng lập phiếu mới.";

/**
 * Thuế suất máy chủ SẼ dùng để định giá các dòng tiền mới của một đơn — bản sao
 * của `orderVATRateBps` (backend/internal/core/order/vat.go).
 *
 * Đơn in ăn thuế suất của ĐƠN GỐC, không phải của `vat_config` hiện tại (quyết
 * định 0027: phát sinh chịu VAT theo thuế suất của đơn, chụp từng dòng). Đổi
 * chỗ này sang đọc cấu hình hệ thống là đơn cũ tự tăng tiền.
 */
export function orderVATRateBps(order: {
  vatEnabled?: boolean | null;
  vatRateBps?: number | null;
}): number {
  if (!order.vatEnabled) return 0;
  return clampRateBps(order.vatRateBps ?? 0);
}

/** Số tạm tính của một đơn in chưa tạo, tách phần thuế. */
export type PrintOrderQuote = {
  /** Tiền hàng trước giảm giá, chưa VAT. */
  goods: number;
  /** Phí ship, chưa VAT. 0 khi khách tự lấy hoặc được miễn. */
  ship: number;
  discount: number;
  /** Thuế suất của ĐƠN GỐC theo điểm cơ bản (800 = 8%). */
  vatRateBps: number;
  vat: number;
  /** Số khách trả, đã gồm VAT — con số phải khoe ra màn hình. */
  payable: number;
};

/**
 * Tạm tính cho form tạo đơn in, dựng theo ĐÚNG cách máy chủ ghi tiền
 * (`printorder.writeLines`): hai dòng riêng — hàng hoá đã trừ giảm giá, và
 * ship — mỗi dòng chụp VAT của mình, `payable = Σ(amount + vat)`.
 *
 * Vì sao không cộng gộp rồi nhân thuế một lần: máy chủ làm tròn nửa lên trên
 * TỪNG dòng, nên một phép nhân duy nhất lệch một đồng ở những cặp số mà hai
 * lần làm tròn không trùng một lần — và số lệch ấy rơi đúng vào ô "phải thu"
 * mà lễ tân đọc cho khách.
 *
 * Vẫn là số TẠM: số chốt do máy chủ trả (`payable` của đơn in vừa tạo).
 */
export function quotePrintOrder(input: {
  goods: number;
  ship: number;
  discount: number;
  vatRateBps: number;
}): PrintOrderQuote {
  const rate = clampRateBps(input.vatRateBps);
  const goodsLine = Math.max(0, input.goods - input.discount);
  const shipLine = Math.max(0, input.ship);
  const vat = lineVAT(goodsLine, rate) + lineVAT(shipLine, rate);
  return {
    goods: input.goods,
    ship: shipLine,
    discount: input.discount,
    vatRateBps: rate,
    vat,
    payable: goodsLine + shipLine + vat,
  };
}

/**
 * Link ảnh in đã dùng được chưa — bản sao nhẹ của `validatePrintLink`
 * (backend/internal/core/cskh/printorder/service.go). Rỗng là HỢP LỆ: rất
 * nhiều đơn chốt tiền trước, ảnh xuất sau.
 *
 * Trần 2048 ký tự khớp `maxPrintLinkLen` của máy chủ — chốt chặn ngón tay
 * trượt (dán cả khối văn bản vào ô link), không phải luật nghiệp vụ.
 */
export function isPrintLinkDraftValid(raw: string): boolean {
  const link = raw.trim();
  if (!link) return true;
  if (link.length > 2048) return false;
  try {
    const u = new URL(link);
    return (u.protocol === "http:" || u.protocol === "https:") && !!u.host;
  } catch {
    return false;
  }
}

export type PoPayBadge = {
  label: string;
  variant: "default" | "secondary" | "destructive" | "outline";
};

/** Nhãn tiền dùng chung cho bảng + chi tiết: huỷ > chờ duyệt > từ chối > đã/chưa thu. */
export function poPayBadge(po: CskhPrintOrder): PoPayBadge {
  if (po.cancelledAt) return { label: "Đã huỷ", variant: "outline" };
  if (!po.legacy && po.goodsStatus === "pending_approval") {
    return { label: "Chờ duyệt giảm giá", variant: "secondary" };
  }
  if (!po.legacy && po.goodsStatus === "rejected") {
    return { label: "Giảm giá bị từ chối", variant: "destructive" };
  }
  return po.paid
    ? { label: "Đã thu", variant: "default" }
    : { label: "Chưa thu", variant: "destructive" };
}

/**
 * Đơn in còn phải thu — cửa của chip "Chưa thu tiền" trên màn Đơn in.
 *
 * Chưa huỷ và chưa thu, gồm cả bản cũ lẫn đơn đang chờ / bị từ chối giảm giá:
 * bản vẽ CEO duyệt 01/09/2026 đếm cả những dòng đó. Đơn đã thu rồi hoàn tiền
 * vẫn giữ cờ đã thu (mốc là lịch sử) nên không lọt vào đây.
 */
export function isPoUnpaid(po: CskhPrintOrder): boolean {
  return !po.cancelledAt && !po.paid;
}

/**
 * Đơn in này có khoản GIẢM GIÁ đang chờ duyệt không.
 *
 * Đơn in có `discount > 0` đẻ ra dòng hàng `pending_approval` (BE writeLines),
 * và cửa thu tiền đóng tới khi dòng đó được duyệt — nên đây cũng là vị từ
 * quyết định có vẽ nút Duyệt/Từ chối hay không.
 *
 * Bốn vế khớp đúng luật máy chủ dựng `goodsStatus` (printorder applyMoney):
 * bản cũ và đơn đã huỷ luôn mang goodsStatus rỗng nên không bao giờ lọt vào
 * đây; `surchargeId` là thứ nút thật sự gọi tới, thiếu nó thì không vẽ nút —
 * dữ liệu lệch còn hơn nút bấm vào hư không.
 */
export function hasPendingDiscount(po: CskhPrintOrder): boolean {
  return !po.legacy && !po.cancelledAt && po.goodsStatus === "pending_approval" && !!po.surchargeId;
}

/** Hai khoá riêng của bộ lọc người chào — không trùng tên người thật được. */
export const SELLER_PORTAL = "__portal";
export const SELLER_NONE = "__none";

/**
 * sellerKey — gom một đơn in về đúng "người chào" của nó.
 *
 * Ba nhóm, cố ý tách rời: khách tự mua qua cổng (không ai được ghi công),
 * đơn CHƯA GHI người chào (phần lớn là đơn di trú từ ERP cũ), và tên người
 * thật. Gộp hai nhóm đầu làm một là nói dối: "chưa ghi" còn sửa được, còn
 * "khách tự mua" thì đúng là không có người chào nào.
 */
/**
 * Đơn in máy lập từ món khách mua thêm trên Cổng (QĐ 0519): phiếu việc, không
 * nối dòng tiền — tiền nằm ở khối Mua thêm của đơn. Máy chủ trả `legacy` như
 * đơn bản cũ (không có dòng phát sinh) nhưng đây KHÔNG phải đơn cũ: màn phải
 * nói đúng tên nó, không dán nhãn "bản cũ — ngoài sổ".
 */
export function isAddonPrint(po: CskhPrintOrder): boolean {
  return po.source === "photo_addon";
}

export const ADDON_PRINT_LABEL = "Khách mua thêm trên Cổng";
export const ADDON_PRINT_MONEY_NOTE =
  "Khách đã trả tiền món này ở khối Mua thêm của đơn — đơn in này không thu thêm.";

export function sellerKey(po: CskhPrintOrder): string {
  // Đơn cổng CÓ người chào là đơn nhân viên đặt hộ (QĐ 0290) — đứng tên người
  // đó, cùng luật với sellerKeySQL phía máy chủ.
  if (po.source === "customer_portal" && !po.sellerName?.trim()) return SELLER_PORTAL;
  return po.sellerName?.trim() || SELLER_NONE;
}

/**
 * Nhãn "… đặt hộ qua Cổng" (QĐ 0290) — rỗng khi khách tự đặt hoặc đơn lập ở ERP.
 */
export function actingLabel(po: Pick<CskhPrintOrder, "actingStaffName">): string {
  const name = po.actingStaffName?.trim();
  return name ? `${name} đặt hộ qua Cổng` : "";
}

/** Dòng khung đi cùng một tấm ảnh — để màn ghi "khung đi cùng" dưới số ảnh. */
export function isFrameLine(productId: string): boolean {
  const s = productId.normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase();
  return s.includes("khung") && !s.includes("album");
}

/** sellerLabel — chữ hiện trên màn cho một khoá của `sellerKey`. */
export function sellerLabel(key: string): string {
  if (key === SELLER_PORTAL) return "Khách tự mua";
  if (key === SELLER_NONE) return "Chưa ghi người chào";
  return key;
}

/**
 * sellerOptions — các lựa chọn của bộ lọc người chào, suy từ CHÍNH tập đơn
 * đang xem: người nghỉ việc mà còn đơn thì vẫn lọc được, còn người chưa bán
 * đơn nào thì không bày ra cho chọn rồi trả về danh sách trống.
 *
 * Tên người xếp A→Z theo tiếng Việt; hai nhóm không-có-người-chào luôn nằm
 * cuối để không chen vào giữa danh sách tên.
 */
export function sellerOptions(
  rows: CskhPrintOrder[],
): { key: string; label: string; count: number }[] {
  const dem: Record<string, number> = {};
  for (const po of rows) {
    const k = sellerKey(po);
    dem[k] = (dem[k] ?? 0) + 1;
  }
  return sellerOptionsFromCounts(dem);
}

/**
 * sellerOptionsFromCounts — cùng thứ tự với `sellerOptions`, nhưng đọc số đếm
 * máy chủ trả (`sellerCounts` của GET /print-orders?limit=…) thay vì tự đếm.
 */
export function sellerOptionsFromCounts(
  counts: Record<string, number>,
): { key: string; label: string; count: number }[] {
  const dacBiet = [SELLER_PORTAL, SELLER_NONE];
  return Object.entries(counts)
    .map(([key, count]) => ({ key, label: sellerLabel(key), count }))
    .sort((a, b) => {
      const ra = dacBiet.indexOf(a.key);
      const rb = dacBiet.indexOf(b.key);
      if (ra !== rb) return (ra === -1 ? -1 : ra) - (rb === -1 ? -1 : rb);
      return a.label.localeCompare(b.label, "vi");
    });
}
